/**
 * Mobile petty-cash expense form — touch-first, single-column.
 * Used by site staff inside the Capacitor Android app.
 *
 * Iteration · 2026-02 fixes:
 *  • Dynamic categories loaded from `/petty-cash/categories` (editable in
 *    Settings → Petty Cash Categories).
 *  • Combined cost-centre picker: site projects + offices via
 *    `/petty-cash/assignees` (so company-wide expenses can be tagged to a
 *    head/branch office instead of forcing a fake project).
 *  • Bill image is re-encoded to JPEG (max 2000 px wide, q≈0.85) on the device
 *    before upload, so iOS HEIC photos and 4K shots both submit cleanly.
 *
 * Iter 140 — Server-side fraud scan:
 *  • The bill image is attached inline to the submit payload (base64).
 *  • The fraud scan runs ASYNCHRONOUSLY on the server, AFTER the row is saved.
 *  • The submitter NEVER sees a risk score, band, or validation report —
 *    those live on the approver / finance / MD dashboards only.
 *  • No "Run AI Bill Scan" button, no risk-score panel.
 */
import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';
import { ArrowLeft, Camera, MapPin, Loader2, Send } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { api, apiPost } from '../../lib/api';
import { enqueue, cachedGet, remainingPhotoSlots } from '../../lib/offlineQueue';
import OfflineReadyBadge from '../../components/OfflineReadyBadge';

/**
 * Re-encode the picked image to JPEG via a canvas. Returns a `File`.
 * Handles:
 *   • iPhone HEIC photos (browser decodes natively into <img>)
 *   • 20 MB camera shots that would otherwise hit the 8 MB backend cap
 *   • EXIF orientation (drawn upright by Canvas)
 */
async function compressImage(file, { maxDim = 2000, quality = 0.85 } = {}) {
  if (!file) return file;
  // Already small + JPEG → skip re-encode (avoids quality loss).
  if (file.type === 'image/jpeg' && file.size < 1_500_000) return file;
  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('Image decode failed (possibly HEIC on a non-Safari browser).'));
      i.src = dataUrl;
    });
    const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.round(img.width * ratio);
    const h = Math.round(img.height * ratio);
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob) return file;
    return new File([blob], (file.name || 'bill').replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch (e) {
    // If decode failed (e.g. HEIC on Chrome Android), fall back to the original
    // file — backend will reject with a clear error which we surface to the user.
    console.warn('Image compress failed, sending original:', e);
    return file;
  }
}

export default function MobilePettyCash() {
  const nav = useNavigate();
  const [assignees, setAssignees] = useState({ projects: [], offices: [] });
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    cost_centre_kind: 'project', // 'project' | 'office'
    project_id: '',
    office_location_id: '',
    category: '',
    estimated_amount: 0, actual_amount: 0,
    reason: '', priority: 'Routine', vendor_name: '', payment_mode: 'Cash',
    expense_date: new Date().toISOString().slice(0, 10),
    gps_lat: null, gps_lng: null,
  });
  const [bill, setBill] = useState(null);
  const [ocr, setOcr] = useState(null); // null | 'reading' | 'failed' | {extracted}
  const [dupReason, setDupReason] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const billCameraRef = useRef(null);

  useEffect(() => {
    cachedGet('petty-cash-assignees', '/petty-cash/assignees').then(({ data: r }) => {
      setAssignees({
        projects: Array.isArray(r?.projects) ? r.projects : [],
        offices: Array.isArray(r?.offices) ? r.offices : [],
      });
    }).catch(() => { /* silent — picker just stays empty */ });
    cachedGet('petty-cash-categories', '/petty-cash/categories').then(({ data: r }) => {
      const cats = Array.isArray(r?.items) ? r.items : [];
      setCategories(cats);
      if (cats.length) setForm((f) => f.category ? f : ({ ...f, category: cats[0].name }));
    }).catch(() => { /* silent */ });
  }, []);

  // Auto-capture GPS once
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm(f => ({ ...f, gps_lat: pos.coords.latitude, gps_lng: pos.coords.longitude })),
      () => { /* user denied — keep null */ },
      { maximumAge: 60000, timeout: 7000 },
    );
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onPickBill = async (e) => {
    const raw = e.target.files?.[0];
    if (!raw) return;
    // Compress on-device so we never hit the 8 MB backend cap and the bill
    // image is small enough to embed in the submit payload as base64.
    const compressed = await compressImage(raw);
    setBill(compressed);
    // Clear the input so the user can re-pick the same file later.
    e.target.value = '';
    setDupReason(null);
    setOcr(null);
    setForm(f => ({ ...f, invoice_number: null, gst_number: null }));

    // OCR auto-fill — the scanned bill fills vendor / amount / bill no for the
    // user. Online only; offline entries sync + scan later on the server.
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    setOcr('reading');
    try {
      const fd = new FormData();
      fd.append('file', compressed, compressed.name || 'bill.jpg');
      const { data } = await api.post('/petty-cash/extract-bill', fd, { timeout: 120000 });
      const ex = data?.extracted || {};
      setForm(f => ({
        ...f,
        vendor_name: ex.vendor_name || f.vendor_name,
        actual_amount: Number(ex.total_amount) > 0 ? Number(ex.total_amount) : f.actual_amount,
        invoice_number: ex.invoice_number || null,
        gst_number: ex.gst_number || null,
        // expense_date may only be today or yesterday (backend rule) — adopt
        // the bill date only when it falls inside that window.
        expense_date: (() => {
          if (!ex.invoice_date) return f.expense_date;
          const diff = (new Date().setHours(0, 0, 0, 0) - new Date(ex.invoice_date).setHours(0, 0, 0, 0)) / 86400000;
          return diff >= 0 && diff <= 1 ? ex.invoice_date : f.expense_date;
        })(),
      }));
      setOcr(ex);
      if (data?.is_duplicate) {
        setDupReason(data.duplicate_reason || 'Duplicate bill detected');
        toast.error('Duplicate bill — already in the system');
      } else {
        toast.success('Bill details auto-filled from the photo');
      }
    } catch {
      setOcr('failed'); // non-fatal — user fills the form manually
    }
  };

  // Iter 140 — small helper to base64 the chosen bill for the submit call.
  // The fraud scan runs server-side, asynchronously, AFTER submission. No
  // score, no risk band, no validation report is ever shown to the uploader.
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        // strip the "data:image/jpeg;base64," prefix
        const out = String(r.result || '').split(',')[1] || '';
        resolve(out);
      };
      r.onerror = reject;
      r.readAsDataURL(file);
    });

  // Full data URL (kept intact) for stashing in the offline queue.
  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || ''));
      r.onerror = reject;
      r.readAsDataURL(file);
    });

  const submit = async () => {
    if (form.reason.length < 20) return toast.error('Reason must be at least 20 characters');
    if (form.cost_centre_kind === 'project' && !form.project_id) return toast.error('Pick a project');
    if (form.cost_centre_kind === 'office' && !form.office_location_id) return toast.error('Pick an office location');
    if (!form.vendor_name) return toast.error('Vendor is required');
    if (!form.category) return toast.error('Pick a category');
    if (dupReason) return toast.error('Duplicate bill — submission blocked');
    setSubmitting(true);

    const basePayload = {
      ...form,
      actual_amount: Number(form.actual_amount || 0),
    };

    // Offline path — queue the entry (with the bill image) for later sync. The
    // server-side fraud scan still runs on sync; the submitter sees no AI signal.
    const queueOffline = async () => {
      let photos = [];
      if (bill) {
        const remaining = await remainingPhotoSlots();
        if (remaining < 1) {
          toast.error('Offline storage full (20 photos) — sync pending entries first.');
          return false;
        }
        try {
          photos = [{ dataUrl: await fileToDataUrl(bill), mime: bill.type || 'image/jpeg' }];
        } catch { /* submit without the bill if it can't be read */ }
      }
      await enqueue('petty_cash', { payload: basePayload, photos });
      toast.success('Saved offline — will sync when you reconnect');
      nav('/m');
      return true;
    };

    try {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        await queueOffline();
        setSubmitting(false);
        return;
      }
      const payload = { ...basePayload };
      if (bill) {
        try {
          payload.bill_image_base64 = await fileToBase64(bill);
          payload.bill_image_mime = bill.type || 'image/jpeg';
        } catch { /* submit without it */ }
      }
      await apiPost('/petty-cash/requests', payload);
      toast.success('Expense submitted for approval');
      nav('/m');
    } catch (e) {
      if (!e?.response && await queueOffline()) { setSubmitting(false); return; }
      const detail = e?.response?.data?.detail || 'Failed';
      toast.error(typeof detail === 'string' ? detail : 'Failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-40" data-testid="mobile-petty-cash">
      {/* Mobile header */}
      <div className="bg-[#3b4872] text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link to="/m" className="p-1.5 -ml-1.5"><ArrowLeft size={20} /></Link>
        <div>
          <div className="font-display font-bold">Submit Expense</div>
          <div className="text-[10px] text-slate-300">Petty cash · auto GPS</div>
        </div>
        {form.gps_lat && <span className="ml-auto text-[10px] flex items-center gap-1 text-emerald-300"><MapPin size={11} /> GPS locked</span>}
      </div>

      <div className="p-4 space-y-3">
        <Section label="Bill Photo">
          {/* Camera capture ONLY — gallery is intentionally disabled so old/reused
              bill photos can't be uploaded, preserving the receipt-verification
              (anti-fraud) control. Offline simply queues the camera-captured image. */}
          <input ref={billCameraRef} hidden type="file" accept="image/*" capture="environment" onChange={onPickBill} />
          <button onClick={() => billCameraRef.current?.click()} className="w-full bg-white border-2 border-dashed border-slate-300 rounded-sm p-5 text-center" data-testid="mp-bill-capture">
            <Camera size={24} className="mx-auto text-slate-400 mb-1" />
            <div className="text-xs font-display font-bold">Capture Bill Photo</div>
            <div className="text-[10px] text-slate-400 mt-0.5">Camera only · no gallery uploads</div>
          </button>
          {bill && (
            <div className="mt-2 text-[11px] text-slate-600">
              Picked: <span className="font-mono">{bill.name}</span> · {(bill.size / 1024).toFixed(0)} KB
              <span className="ml-2 text-emerald-700">— attached to submission</span>
            </div>
          )}
          {ocr === 'reading' && (
            <div className="mt-2 flex items-center gap-2 text-[11px] text-indigo-700" data-testid="mp-ocr-reading">
              <Loader2 size={12} className="animate-spin" /> Reading bill details from the photo…
            </div>
          )}
          {ocr === 'failed' && (
            <div className="mt-2 text-[11px] text-amber-700">Couldn't read the bill — fill the details manually.</div>
          )}
          {ocr && typeof ocr === 'object' && (
            <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-sm p-2 text-[11px] text-emerald-800" data-testid="mp-ocr-summary">
              Auto-filled from bill: <strong>{ocr.vendor_name || '—'}</strong> · ₹{ocr.total_amount ?? '—'} · Bill #{ocr.invoice_number || '—'}
            </div>
          )}
          {dupReason && (
            <div className="mt-2 bg-rose-50 border border-rose-200 rounded-sm p-2 text-[11px] text-rose-700 font-semibold" data-testid="mp-duplicate-banner">
              ⛔ {dupReason} Submission is blocked.
            </div>
          )}
        </Section>

        <Section label="Cost Centre">
          <div className="grid grid-cols-2 gap-2 mb-2">
            {['project', 'office'].map(k => (
              <button
                key={k}
                type="button"
                onClick={() => set('cost_centre_kind', k)}
                className={`px-3 py-2 text-xs font-display font-bold uppercase tracking-wider border rounded-sm ${form.cost_centre_kind === k ? 'border-[#3b4872] bg-indigo-50 text-[#3b4872]' : 'border-slate-300 text-slate-500'}`}
                data-testid={`mp-kind-${k}`}
              >
                {k === 'project' ? 'Site Project' : 'Office / Other'}
              </button>
            ))}
          </div>
          {form.cost_centre_kind === 'project' ? (
            <select value={form.project_id} onChange={(e) => set('project_id', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="mp-project">
              <option value="">Select project…</option>
              {assignees.projects.map(p => <option key={p.id} value={p.id}>{p.name}{p.code ? ` (${p.code})` : ''}</option>)}
            </select>
          ) : (
            <select value={form.office_location_id} onChange={(e) => set('office_location_id', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="mp-office">
              <option value="">Select office…</option>
              {assignees.offices.map(o => <option key={`${o.source || 'cm'}-${o.id}`} value={o.id}>{o.name}{o.city ? ` · ${o.city}` : ''}</option>)}
            </select>
          )}
        </Section>

        <div className="grid grid-cols-2 gap-2">
          <Section label="Category">
            <select value={form.category} onChange={(e) => set('category', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="mp-category">
              <option value="">Select…</option>
              {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </Section>
          <Section label="Priority">
            <select value={form.priority} onChange={(e) => set('priority', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white">
              <option>Routine</option><option>Urgent</option><option>Emergency</option>
            </select>
          </Section>
        </div>

        <Section label="Actual Amount ₹">
          <input type="number" inputMode="decimal" value={form.actual_amount} onChange={(e) => set('actual_amount', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="mp-amount" />
        </Section>

        <Section label="Vendor">
          <input value={form.vendor_name} onChange={(e) => set('vendor_name', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="mp-vendor" />
        </Section>

        <Section label="Payment Mode">
          <select value={form.payment_mode} onChange={(e) => set('payment_mode', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white">
            <option>Cash</option><option>UPI</option><option>Vendor Wallet</option><option>Fuel Card</option><option>Bank Transfer</option>
          </select>
        </Section>

        <Section label={`Reason (${form.reason.length}/20+)`}>
          <textarea rows={3} value={form.reason} onChange={(e) => set('reason', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="mp-reason" />
        </Section>
      </div>

      {/* Sticky submit — sits ABOVE the global MobileLayout bottom nav
          (z-40, ~5rem tall + iPhone safe-area). Without this offset the
          system tab-bar physically covers the button on every device. */}
      <div
        className="fixed inset-x-0 bg-white border-t border-slate-200 px-4 py-3 z-50"
        style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom))' }}
      >
        <button onClick={submit} disabled={submitting || !!dupReason} className="w-full bg-[#3b4872] hover:bg-[#2d3856] disabled:opacity-50 text-white font-display font-bold rounded-sm py-3 flex items-center justify-center gap-2" data-testid="mp-submit">
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {submitting ? 'Submitting…' : 'Submit Expense'}
        </button>
        <div className="flex justify-center mt-2"><OfflineReadyBadge /></div>
      </div>
    </div>
  );
}

const Section = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest font-display text-slate-500 px-1">{label}</label>
    {children}
  </div>
);
